import { Command, Plugin, Collection } from "ckeditor5";
import {
  addListToDropdown,
  createDropdown,
  ViewModel,
} from "ckeditor5";

const LINE_HEIGHT_OPTIONS = [
  { title: "Default", value: null },
  { title: "1.3", value: "policy-lh-13" },
  { title: "1.35", value: "policy-lh-135" },
  { title: "1.5", value: "policy-lh-15" },
  { title: "1.65", value: "policy-lh-165" },
  { title: "1.7", value: "policy-lh-17" },
  { title: "2", value: "policy-lh-2" },
];

const SPACING_AFTER_OPTIONS = [
  { title: "Default", value: null },
  { title: "0", value: "policy-mb-0" },
  { title: "8px", value: "policy-mb-8" },
  { title: "16px", value: "policy-mb-16" },
  { title: "24px", value: "policy-mb-24" },
  { title: "32px", value: "policy-mb-32" },
  { title: "48px", value: "policy-mb-48" },
];

const SPACING_BEFORE_OPTIONS = [
  { title: "Default", value: null },
  { title: "0", value: "policy-mt-0" },
  { title: "8px", value: "policy-mt-8" },
  { title: "16px", value: "policy-mt-16" },
  { title: "24px", value: "policy-mt-24" },
  { title: "32px", value: "policy-mt-32" },
  { title: "48px", value: "policy-mt-48" },
];

const CLASS_TO_LINE_HEIGHT = {
  "policy-lh-13": "1.3",
  "policy-lh-135": "1.35",
  "policy-lh-15": "1.5",
  "policy-lh-165": "1.65",
  "policy-lh-17": "1.7",
  "policy-lh-2": "2",
};

const CLASS_TO_MARGIN_BOTTOM = {
  "policy-mb-0": "0",
  "policy-mb-8": "8px",
  "policy-mb-16": "16px",
  "policy-mb-24": "24px",
  "policy-mb-32": "32px",
  "policy-mb-48": "48px",
};

const CLASS_TO_MARGIN_TOP = {
  "policy-mt-0": "0",
  "policy-mt-8": "8px",
  "policy-mt-16": "16px",
  "policy-mt-24": "24px",
  "policy-mt-32": "32px",
  "policy-mt-48": "48px",
};

const LINE_HEIGHT_CLASSES = LINE_HEIGHT_OPTIONS.map((o) => o.value).filter(Boolean);
const SPACING_AFTER_CLASSES = SPACING_AFTER_OPTIONS.map((o) => o.value).filter(Boolean);
const SPACING_BEFORE_CLASSES = SPACING_BEFORE_OPTIONS.map((o) => o.value).filter(Boolean);

function getBlockViewName(block) {
  if (!block || block.is("rootElement")) {
    return null;
  }

  if (block.name === "paragraph") {
    return "p";
  }

  if (block.name === "listItem") {
    return "li";
  }

  if (block.name === "bulletedList") {
    return "ul";
  }

  if (block.name === "numberedList") {
    return "ol";
  }

  if (block.name.startsWith("heading")) {
    return `h${block.name.replace("heading", "")}`;
  }

  if (block.name === "htmlDiv" || block.name === "div") {
    return "div";
  }

  if (block.name === "htmlUl") {
    return "ul";
  }

  if (block.name === "htmlOl") {
    return "ol";
  }

  return null;
}

function getListContainerViewName(block) {
  const listType = block.getAttribute("listType");
  if (listType === "numbered" || listType === "customNumbered") {
    return "ol";
  }
  return "ul";
}

function getViewNameForCommand(block, marginCommand) {
  if (marginCommand && block.name === "listItem") {
    return getListContainerViewName(block);
  }

  return getBlockViewName(block);
}

function getSelectedBlocks(model) {
  const selection = model.document.selection;
  const blocks = Array.from(selection.getSelectedBlocks());

  if (blocks.length) {
    return blocks.filter((block) => getBlockViewName(block));
  }

  let element = selection.getFirstPosition()?.parent;

  while (element && !element.is("rootElement")) {
    if (getBlockViewName(element)) {
      return [element];
    }
    element = element.parent;
  }

  return [];
}

function readGhsValue(block, ghs, viewName) {
  const attributeName = ghs.getGhsAttributeNameForElement(viewName);
  return block.getAttribute(attributeName);
}

function readBlockClass(block, ghs, classPool, marginCommand) {
  const viewName = getViewNameForCommand(block, marginCommand);
  if (!viewName) {
    return null;
  }

  const ghsValue = readGhsValue(block, ghs, viewName);
  const classes = ghsValue?.classes || [];

  return classPool.find((className) => classes.includes(className)) || null;
}

class BlockTypographyCommand extends Command {
  constructor(editor, { classPool, styleProperty, classToStyle, marginCommand = false }) {
    super(editor);
    this.classPool = classPool;
    this.styleProperty = styleProperty;
    this.classToStyle = classToStyle;
    this.marginCommand = marginCommand;
  }

  refresh() {
    const blocks = getSelectedBlocks(this.editor.model);
    this.isEnabled = blocks.length > 0;

    if (!blocks.length) {
      this.value = null;
      return;
    }

    const ghs = this.editor.plugins.get("GeneralHtmlSupport");
    const block = blocks[0];
    const viewName = getViewNameForCommand(block, this.marginCommand);
    const fromClass = readBlockClass(block, ghs, this.classPool, this.marginCommand);

    if (fromClass) {
      this.value = fromClass;
      return;
    }

    if (this.styleProperty && viewName) {
      const ghsValue = readGhsValue(block, ghs, viewName);
      const styleValue = ghsValue?.styles?.[this.styleProperty];
      if (styleValue) {
        const match = Object.entries(this.classToStyle).find(
          ([, value]) => value === styleValue
        );
        this.value = match ? match[0] : null;
        return;
      }
    }

    this.value = null;
  }

  execute({ value } = {}) {
    const ghs = this.editor.plugins.get("GeneralHtmlSupport");
    const blocks = getSelectedBlocks(this.editor.model);

    for (const block of blocks) {
      const viewName = getViewNameForCommand(block, this.marginCommand);
      if (!viewName) {
        continue;
      }

      ghs.removeModelHtmlClass(viewName, this.classPool, block);

      if (this.styleProperty) {
        ghs.removeModelHtmlStyles(viewName, this.styleProperty, block);
      }

      if (value) {
        ghs.addModelHtmlClass(viewName, value, block);

        const styleValue = this.classToStyle?.[value];
        if (this.styleProperty && styleValue) {
          ghs.setModelHtmlStyles(
            viewName,
            { [this.styleProperty]: styleValue },
            block
          );
        }
      }
    }
  }
}

function createBlockStyleDropdown(editor, { commandName, label, options }) {
  const command = editor.commands.get(commandName);
  const itemDefinitions = new Collection();

  for (const option of options) {
    itemDefinitions.add({
      type: "button",
      model: new ViewModel({
        commandName,
        commandParam: option.value,
        label: option.title,
        withText: true,
        role: "menuitemradio",
      }),
    });
  }

  editor.ui.componentFactory.add(commandName, (locale) => {
    const dropdownView = createDropdown(locale);

    addListToDropdown(dropdownView, itemDefinitions, {
      role: "menu",
      ariaLabel: label,
    });

    dropdownView.buttonView.set({
      label,
      withText: true,
      tooltip: true,
    });

    dropdownView.bind("isEnabled").to(command);

    dropdownView.on("execute", (evt) => {
      editor.execute(evt.source.commandName, { value: evt.source.commandParam });
      editor.editing.view.focus();
    });

    return dropdownView;
  });
}

class BlockTypographyEditing extends Plugin {
  static get pluginName() {
    return "BlockTypographyEditing";
  }

  init() {
    this.editor.commands.add(
      "lineHeight",
      new BlockTypographyCommand(this.editor, {
        classPool: LINE_HEIGHT_CLASSES,
        styleProperty: "line-height",
        classToStyle: CLASS_TO_LINE_HEIGHT,
      })
    );
    this.editor.commands.add(
      "paragraphSpacing",
      new BlockTypographyCommand(this.editor, {
        classPool: SPACING_AFTER_CLASSES,
        styleProperty: "margin-bottom",
        classToStyle: CLASS_TO_MARGIN_BOTTOM,
        marginCommand: true,
      })
    );
    this.editor.commands.add(
      "paragraphSpacingBefore",
      new BlockTypographyCommand(this.editor, {
        classPool: SPACING_BEFORE_CLASSES,
        styleProperty: "margin-top",
        classToStyle: CLASS_TO_MARGIN_TOP,
        marginCommand: true,
      })
    );
  }
}

class BlockTypographyUI extends Plugin {
  static get pluginName() {
    return "BlockTypographyUI";
  }

  init() {
    createBlockStyleDropdown(this.editor, {
      commandName: "lineHeight",
      label: "Line height",
      options: LINE_HEIGHT_OPTIONS,
    });

    createBlockStyleDropdown(this.editor, {
      commandName: "paragraphSpacingBefore",
      label: "Space before",
      options: SPACING_BEFORE_OPTIONS,
    });

    createBlockStyleDropdown(this.editor, {
      commandName: "paragraphSpacing",
      label: "Space after",
      options: SPACING_AFTER_OPTIONS,
    });
  }
}

export default class BlockTypography extends Plugin {
  static get pluginName() {
    return "BlockTypography";
  }

  static get requires() {
    return [BlockTypographyEditing, BlockTypographyUI];
  }
}
